/* eslint-disable max-len */
import React from 'react';
import { Box, Typography, Grid, makeStyles } from '@material-ui/core';
import { Broker } from 'models';
import { useIntl } from 'react-intl';
import $ from 'jquery';
import { getPhoneNumberWithFormatting, getColorContrastRatio } from 'utils';
import { COLOR, COLOR_CONTRAST_RATIO } from 'config/constants';
import { isMobile } from 'utils/screen';
import { HvButton } from 'components';
import PortalHeader from './PortalHeader';
import SocialMediaIcons from './SocialMediaIcons';

interface Props {
  broker: Broker;
  slogan: string;
  bio: string;
  profile_picture_url: string;
  logo_picture_url?: string;
  portal_infos: any;
  sections?: any;
  favorite_listings: any;
  is_white_text?: boolean;
}

function BrokerHighlightHeader(props: Props) {
  const {
    broker,
    slogan,
    bio,
    profile_picture_url,
    logo_picture_url,
    portal_infos,
    sections,
    favorite_listings,
    is_white_text,
  } = props;

  const useStyles = makeStyles({
    brokerImageAndBio: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: 160,
      marginBottom: 60,
      alignSelf: 'center',
      '@media (max-width: 800px)': {
        width: '80%',
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
    imageContent: {
      display: 'flex',
      justifyContent: 'center',
    },
    profilePicture: {
      borderRadius: 8,
      width: 300,
      height: 400,
      marginBottom: 10,
      objectFit: 'cover',
      backgroundColor: 'white',
      '@media (max-width: 800px)': {
        width: '100%',
        aspectRatio: 0.75,
      },
    },
    brokerContactInfos: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 20,
      width: '100%',
    },
    brokerContactInfosItem: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 20,
    },
    brokerBio: {
      marginTop: 30,
      minWidth: 300,
      textAlign: 'justify',
      borderRadius: 5,
      backgroundColor: '#f5f5f5',
      whiteSpace: 'pre-line',
      padding: 10,
    },
    socialMediaContent: {
      marginTop: 20,
      display: 'flex',
      flexDirection: 'row',
    },
    sloganContainer: {
      height: '100%',
      backgroundSize: 'cover',
      textAlign: 'center',
      backgroundRepeat: 'no-repeat',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      marginTop: 30,
      marginBottom: 20,
      flexDirection: 'column',
      '@media (max-width: 900px)': {
        height: 300,
      },
    },
    slogan: {
      fontSize: 26,
      fontFamily: 'FordAntenna-SemiBold',
      lineHeight: 2,
      marginBottom: 20,
      maxHeight: 200,
      overflowY: 'auto',
      '@media (max-width: 900px)': {
        fontSize: 12,
      },
    },
    stickyHeader: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: 'auto',
      backgroundColor: COLOR.WHITE,
      zIndex: 999,
    },
    waveContainer: {
      width: '100%',
      height: '100%',
      position: 'relative',
    },
    wave: {
      width: '100%',
      position: 'relative',
    },
    bannerDetail: {
      position: 'absolute',
      top: '45%',
      width: '60%',
      transform: 'translateX(30%)',
      height: '100%',
      '@media (max-width: 900px)': {
        top: '20%',
      },
    },
    buttonText: {
      fontFamily: 'FordAntenna-Medium',
    },
  });

  const styles = useStyles();
  const intl = useIntl();
  const fmtContactMe = intl.formatMessage({ id: 'portal.contactMe' });

  const primary_color = portal_infos?.template_style?.primary_color || COLOR.SECONDARY_BLUE;
  const secondary_color = portal_infos?.template_style?.secondary_color || COLOR.LIGHT_BLUE;

  const primary_color_contrast_ratio = getColorContrastRatio(primary_color, COLOR.WHITE);
  const secondary_color_contrast_ratio = getColorContrastRatio(secondary_color, COLOR.WHITE);
  const secondary_button_text_color =
    primary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;
  const button_text_color = portal_infos?.template_style?.secondary_color
    ? secondary_button_text_color
    : COLOR.WHITE;

  return (
    <Box>
      <Box className={styles.stickyHeader}>
        <PortalHeader
          displaySocialMedia={false}
          logo_picture_url={logo_picture_url}
          portal_infos={portal_infos}
          broker={broker}
          sections={sections}
          favorite_listings={favorite_listings}
          is_white_text={is_white_text}
        />
      </Box>
      <Grid container className={styles.brokerImageAndBio}>
        {profile_picture_url ? (
          <Grid className={styles.imageContent} item md={5}>
            <img className={styles.profilePicture} src={profile_picture_url} alt='Avatar' />
          </Grid>
        ) : null}
        <Grid item md={5}>
          <Typography style={{ fontFamily: 'FordAntenna-SemiBold' }} variant='h5'>
            {portal_infos && portal_infos.display_name
              ? portal_infos.display_name
              : broker.fullName}
          </Typography>
          <Box className={styles.brokerContactInfos}>
            {portal_infos.email && portal_infos.email !== null && (
              <Box className={styles.brokerContactInfosItem}>
                <Typography
                  onClick={() => {
                    window.location.href = `mailto:${portal_infos.email}`;
                  }}
                  style={{ cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
                >
                  {portal_infos.email}
                </Typography>
              </Box>
            )}
            <Box className={styles.brokerContactInfosItem}>
              {portal_infos.phone_number || broker.centrisPhoneNumber ? (
                <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                  {getPhoneNumberWithFormatting(
                    portal_infos.phone_number || broker.centrisPhoneNumber,
                  )}
                </Typography>
              ) : null}
            </Box>
            {bio ? (
              <Box className={styles.brokerBio}>
                <Typography variant='body2'>{bio}</Typography>
              </Box>
            ) : null}
          </Box>
          <Box className={styles.socialMediaContent}>
            <SocialMediaIcons portal_infos={portal_infos} broker={broker} />
          </Box>
        </Grid>
      </Grid>
      <Box
        className={styles.sloganContainer}
        style={{
          color: secondary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK,
        }}
      >
        <div
          className={styles.waveContainer}
          style={{ backgroundColor: isMobile() ? secondary_color : null }}
        >
          {!isMobile() ? (
            <svg
              id='wave'
              className={styles.wave}
              style={{ transform: 'rotate(0deg)', transition: '0.3s' }}
              viewBox='0 0 1440 400'
              version='1.1'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                style={{ transform: 'translate(0, 0px)', opacity: '1' }}
                fill={secondary_color}
                d='M0,49L120,73.5C240,98,480,147,720,138.8C960,131,1200,65,1440,65.3C1680,65,1920,131,2160,130.7C2400,131,2640,65,2880,32.7C3120,0,3360,0,3600,65.3C3840,131,4080,261,4320,310.3C4560,359,4800,327,5040,326.7C5280,327,5520,359,5760,326.7C6000,294,6240,196,6480,196C6720,196,6960,294,7200,318.5C7440,343,7680,294,7920,285.8C8160,278,8400,310,8640,326.7C8880,343,9120,343,9360,334.8C9600,327,9840,310,10080,310.3C10320,310,10560,327,10800,334.8C11040,343,11280,343,11520,343C11760,343,12000,343,12240,318.5C12480,294,12720,245,12960,261.3C13200,278,13440,359,13680,400.2C13920,441,14160,441,14400,383.8C14640,327,14880,212,15120,187.8C15360,163,15600,229,15840,220.5C16080,212,16320,131,16560,106.2C16800,82,17040,114,17160,130.7L17280,147L17280,490L17160,490C17040,490,16800,490,16560,490C16320,490,16080,490,15840,490C15600,490,15360,490,15120,490C14880,490,14640,490,14400,490C14160,490,13920,490,13680,490C13440,490,13200,490,12960,490C12720,490,12480,490,12240,490C12000,490,11760,490,11520,490C11280,490,11040,490,10800,490C10560,490,10320,490,10080,490C9840,490,9600,490,9360,490C9120,490,8880,490,8640,490C8400,490,8160,490,7920,490C7680,490,7440,490,7200,490C6960,490,6720,490,6480,490C6240,490,6000,490,5760,490C5520,490,5280,490,5040,490C4800,490,4560,490,4320,490C4080,490,3840,490,3600,490C3360,490,3120,490,2880,490C2640,490,2400,490,2160,490C1920,490,1680,490,1440,490C1200,490,960,490,720,490C480,490,240,490,120,490L0,490Z'
              />
            </svg>
          ) : null}
          <div className={styles.bannerDetail}>
            <Typography className={styles.slogan}>{slogan || null}</Typography>
            <HvButton
              onClick={(e: any) => {
                e.preventDefault();
                const form = $('.section.contactform')[0];
                if (form) {
                  form.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
              }}
              style={{
                backgroundColor: primary_color,
                color: button_text_color,
              }}
            >
              <Typography className={styles.buttonText}>{fmtContactMe}</Typography>
            </HvButton>
          </div>
        </div>
      </Box>
    </Box>
  );
}

export default BrokerHighlightHeader;
